import React, { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { RadioButton } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { UploadFileInputs } from './UploadFileInput';
import { AppInputField } from '../../../general/AppInputField/AppInputField';

interface Props {
  id: string;
  label: string;
  onChange: (data: {
    name: string;
    value: string | File | object | null;
    secondaryValue?: string | null;
  }) => void;
  isRequired?: boolean;
  labelTextClassName?: string;
  className?: string;
  optionType: string;
  selectedValue: { value: string | File | object | null; secondaryValue?: string | null };
  secondaryText?: string;
  defaultOption?: 'Yes' | 'No';
  errorText?: string;
  currentValue?: string | null;
}

const ConditionalOption: React.FC<Props> = ({
  id,
  label,
  onChange,
  isRequired = false,
  labelTextClassName,
  className = 'flex flex-col gap-1',
  optionType,
  selectedValue,
  secondaryText = '',
  defaultOption,
  errorText,
  currentValue,
}) => {
  const isInit = useRef(true);

  const [selectedOption, setSelectedOption] = useState<'Yes' | 'No' | ''>('');
  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    if (!isInit.current) return;

    const hasSecondary =
      selectedValue.secondaryValue === 'Yes' || selectedValue.secondaryValue === 'No';

    if (!hasSecondary && !defaultOption && !currentValue) return;

    const option =
      selectedValue.value === 'No'
        ? 'No'
        : selectedValue.secondaryValue || defaultOption || (currentValue ? 'Yes' : '');

    setSelectedOption(option as 'Yes' | 'No');

    if (optionType === 'text' && typeof selectedValue.value === 'string' && currentValue !== 'No') {
      onChange({
        name: 'text',
        value: selectedValue.value,
        secondaryValue: 'Yes',
      });
    }

    if (optionType === 'file' && currentValue && currentValue !== 'No') {
      setFileName(currentValue);
    }

    isInit.current = false;
  }, [selectedValue.secondaryValue, selectedValue.value, defaultOption, currentValue, optionType]);

  useEffect(() => {
    if (!selectedOption) return;

    if (selectedOption === 'No') {
      onChange({
        name: optionType,
        value: 'No',
        secondaryValue: 'No',
      });
      return;
    }

    onChange({
      name: optionType,
      value: selectedValue.value !== 'No' ? selectedValue.value : '',
      secondaryValue: 'Yes',
    });
  }, [selectedOption]);

  return (
    <div>
      {(label || isRequired) && (
        <label
          htmlFor={id}
          className={twMerge('text-xs mb-1 text-left font-medium', labelTextClassName)}>
          {isRequired && <span className="text-red-500">*</span>}
          {label}
        </label>
      )}

      <div className={className}>
        {(['Yes', 'No'] as const).map((option) => (
          <div
            key={option}
            className="flex items-center mb-1 cursor-pointer"
            onClick={() => setSelectedOption(option)}>
            <RadioButton
              weight={selectedOption === option ? 'fill' : 'thin'}
              size={20}
              color={selectedOption === option ? COLORS.PRIMARY_500 : COLORS.GREY_500}
            />
            <div className="ml-1 text-xs">{option}</div>
          </div>
        ))}
      </div>

      {selectedOption === 'Yes' && (
        <div className="flex w-full items-start justify-start mt-2">
          {optionType === 'file' && (
            <UploadFileInputs
              label={secondaryText}
              isRequired={isRequired}
              onSelectFile={(data) =>
                onChange({ name: 'file', value: data.file, secondaryValue: 'Yes' })
              }
              selectedFile={selectedValue.value instanceof File ? selectedValue.value : null}
              selectedFileName={fileName}
            />
          )}

          {optionType === 'text' && (
            <AppInputField
              label={secondaryText}
              isRequired={isRequired}
              value={typeof selectedValue.value === 'string' ? selectedValue.value : ''}
              onTextChange={(value) => onChange({ name: 'text', value, secondaryValue: 'Yes' })}
            />
          )}
        </div>
      )}

      {errorText && (
        <p className="text-2xs mt-0.5 text-red-500">
          {optionType === 'file' ? 'Please upload a file' : 'Please enter text'}
        </p>
      )}
    </div>
  );
};

export default ConditionalOption;
